import { DetailHeader, ErrorState } from "@elyxeer/ui";
import AlarmOutlinedIcon from "@mui/icons-material/AlarmOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { DayDoses } from "~/components/routine/DayDoses";
import { RoutineCalendar } from "~/components/routine/RoutineCalendar";
import {
  useRoutineCheckIns,
  useRoutineStreak,
} from "~/components/routine/use-routine";
import { Screen } from "~/components/ui/Screen";
import { INTL_LOCALE, useLocale } from "~/i18n";
import { dayKey, monthWindow, startOfMonth } from "~/lib/calendar";
import {
  addDays,
  clockTimeAsDate,
  indexCheckIns,
  isEditableDay,
  isFutureDay,
  reminderSettings,
  type RemindersLinkState,
  ROUTINE_SLOTS,
} from "~/lib/routine";
import { useToday } from "~/lib/use-today";
import { useAuthStore } from "~/store/auth-store";

/**
 * My routine — the whole habit on one screen.
 *
 * The selected day's two doses on top (today unless another day is picked), the month
 * underneath with a dot per dose, and the reminders at the foot, because "when am I told"
 * is the question a member asks right after "how am I doing".
 *
 * Selection and month are held as "follow today" (`null`) until the member picks
 * something else, so a screen left open over midnight moves on to the new day by itself.
 */
export function RoutineRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const intlLocale = INTL_LOCALE[locale];
  const today = useToday();
  const user = useAuthStore((state) => state.user);

  const [pickedDay, setPickedDay] = useState<Date | null>(null);
  const [pickedMonth, setPickedMonth] = useState<Date | null>(null);
  const day = pickedDay ?? today;
  const month = pickedMonth ?? startOfMonth(today);

  const grid = monthWindow(month);
  const { data, isPending, isError, refetch } = useRoutineCheckIns(
    grid.from,
    // `monthWindow` ends at the midnight after the grid; the API's `to` is inclusive.
    addDays(grid.to, -1),
  );
  const { data: streak } = useRoutineStreak(today);
  const index = indexCheckIns(data ?? []);

  const selectDay = (next: Date) =>
    setPickedDay(dayKey(next) === dayKey(today) ? null : next);

  const changeMonth = (next: Date) => {
    if (startOfMonth(next).getTime() === startOfMonth(today).getTime()) {
      setPickedMonth(null);
      setPickedDay(null);
      return;
    }
    setPickedMonth(next);
    // The month's last day, so the tiles always show a day the grid is drawing.
    setPickedDay(new Date(next.getFullYear(), next.getMonth() + 1, 0));
  };

  const dayLabel =
    dayKey(day) === dayKey(today)
      ? t("routine.today")
      : dayKey(day) === dayKey(addDays(today, -1))
        ? t("routine.yesterday")
        : new Intl.DateTimeFormat(intlLocale, {
            weekday: "long",
            day: "numeric",
            month: "long",
          }).format(day);
  const closed = !isEditableDay(day, today) && !isFutureDay(day, today);

  const timeFormat = new Intl.DateTimeFormat(intlLocale, {
    hour: "numeric",
    minute: "2-digit",
  });
  const reminders = user ? reminderSettings(user) : null;
  const reminderSummary = reminders
    ? ROUTINE_SLOTS.filter((slot) => reminders[slot].enabled)
        .map((slot) => {
          const time = clockTimeAsDate(reminders[slot].time);
          return `${t(`routine.${slot}`)} ${time ? timeFormat.format(time) : ""}`;
        })
        .join(" · ") || t("routine.remindersOff")
    : "";

  return (
    // `sm`: a month of square cells at reading width would be a wall of 110px boxes.
    <Screen maxWidth="sm">
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}
      >
        <DetailHeader
          backTo="/news"
          backLabel={t("routine.backToDashboard")}
          backTestId="routine.back"
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            {t("routine.title")}
          </Typography>
          <Typography variant="T2" sx={{ color: "text.secondary" }}>
            {t("routine.intro")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography
              variant="H3"
              sx={{
                color: "text.primary",
                // Czech weekday names are lower-case; H3 is sentence case.
                "&::first-letter": { textTransform: "uppercase" },
              }}
              data-testid="routine.selected-day"
            >
              {dayLabel}
            </Typography>
            <Typography
              variant="L2"
              sx={{ color: "text.secondary", flexShrink: 0 }}
              data-testid="routine.streak"
            >
              {streak ? t("routine.streakValue", { days: streak.days }) : null}
            </Typography>
          </Box>

          <DayDoses
            day={day}
            today={today}
            doses={index.get(dayKey(day))}
            loading={isPending || isError}
          />

          {closed ? (
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("routine.readOnly")}
            </Typography>
          ) : null}
        </Box>

        {isError ? (
          <ErrorState
            message={t("routine.error")}
            onRetry={() => void refetch()}
          />
        ) : null}

        <RoutineCalendar
          month={month}
          today={today}
          selected={day}
          index={index}
          onSelect={selectDay}
          onMonthChange={changeMonth}
        />

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <ListItemButton
            component={RouterLink}
            to="/profile/reminders"
            state={{ from: "routine" } satisfies RemindersLinkState}
            data-testid="routine.reminders"
            sx={{ gap: 1, paddingBlock: 1.75, paddingInline: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "link.main" }}>
              <AlarmOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("routine.remindersLink")}
              secondary={reminderSummary}
              slotProps={{
                primary: { variant: "L1", sx: { color: "text.primary" } },
                secondary: {
                  variant: "T3",
                  sx: { color: "text.muted", marginTop: 0.25 },
                },
              }}
            />
            <ChevronRightIcon
              sx={{
                color: "text.muted",
                flexShrink: 0,
                fontSize: 24,
                marginLeft: "auto",
              }}
            />
          </ListItemButton>
        </Paper>
      </Box>
    </Screen>
  );
}
